import {Alert, Stack} from '@mantine/core';
import React from 'react';

import {PlateSchema} from 'samples/storables/StorableSchemaConstants';

import PlateMetadataFieldsSection from './PlateMetadataFieldsSection';
import PlateSchemaFieldsSection from './PlateSchemaFieldsSection';
import {
  getIncompatiblePlateSchemaFieldNames,
  PlateMetadataFieldEntry,
  PlateMetadataFieldsAction,
} from './ScratchPlateDesignModalUtil';

export interface PlateLevelFieldsSectionProps {
  plateSchema: PlateSchema | null;
  /**
   * Plate-level metadata fields, rendered when plateSchema is null. Omit both this and
   * onPlateMetadataFieldsAction to hide the section when no schema is selected.
   */
  plateMetadataFields?: Array<PlateMetadataFieldEntry>;
  onPlateMetadataFieldsAction?: (action: PlateMetadataFieldsAction) => void;
  /**
   * Values for the selected plate schema's own fields, rendered instead of plateMetadataFields when
   * plateSchema is set. Omit both this and onPlateSchemaFieldValuesAction to hide the section when
   * a schema is selected.
   */
  plateSchemaFieldValues?: Array<PlateMetadataFieldEntry>;
  onPlateSchemaFieldValuesAction?: (action: PlateMetadataFieldsAction) => void;
}

/**
 * Renders whichever plate-level field section matches the current schema state — schema field
 * values when a schema is selected, ad hoc metadata fields otherwise. The two are mutually
 * exclusive, matching how the underlying field values are stored (see getActivePlateFields).
 */
export default function PlateLevelFieldsSection({
  plateSchema,
  plateMetadataFields,
  onPlateMetadataFieldsAction,
  plateSchemaFieldValues,
  onPlateSchemaFieldValuesAction,
}: PlateLevelFieldsSectionProps): JSX.Element | null {
  if (plateSchema != null) {
    const incompatibleFieldNames = getIncompatiblePlateSchemaFieldNames(plateSchema);
    const hasFieldsSection = plateSchemaFieldValues != null && onPlateSchemaFieldValuesAction != null;
    if (incompatibleFieldNames.length === 0 && !hasFieldsSection) {
      return null;
    }
    return (
      <Stack spacing="xs">
        {plateSchemaFieldValues != null && onPlateSchemaFieldValuesAction != null && (
          <PlateSchemaFieldsSection
            fields={plateSchemaFieldValues}
            onAction={onPlateSchemaFieldValuesAction}
          />
        )}
        {incompatibleFieldNames.length > 0 && (
          <Alert variant="warning">
            {`Some plate schema fields cannot be configured until after the plate has been put in inventory due to their field types: ${incompatibleFieldNames.join(', ')}. Support for these field types is coming soon.`}
          </Alert>
        )}
      </Stack>
    );
  }
  return plateMetadataFields != null && onPlateMetadataFieldsAction != null ? (
    <PlateMetadataFieldsSection fields={plateMetadataFields} onAction={onPlateMetadataFieldsAction} />
  ) : null;
}
